/**
 * Role-keyed quick actions + grouped starter questions for AskBot.
 * Used by QuickActions, SuggestedQuestions, and the autocomplete input helper.
 */

type RoleKey = "bh" | "manager" | "field";

export interface QuickAction {
  id: string;
  label: string;
  query: string;
  emoji: string;
}

export interface SuggestedGroup {
  category: string;
  questions: string[];
}

const BH_ROLES = new Set(["admin", "super_admin", "business_head"]);
const MANAGER_ROLES = new Set([
  "sales_manager",
  "branch_manager",
  "operations_user",
  "operational_user",
]);

function getRoleKey(primaryRole: string): RoleKey {
  if (BH_ROLES.has(primaryRole)) return "bh";
  if (MANAGER_ROLES.has(primaryRole)) return "manager";
  return "field";
}

const QUICK_ACTIONS: Record<RoleKey, QuickAction[]> = {
  field: [
    { id: "my-leads",    label: "My Leads",   query: "Show my active leads",                      emoji: "📋" },
    { id: "pending",     label: "Pending",     query: "Show my pending follow-up leads",           emoji: "⏳" },
    { id: "disbursed",   label: "Disbursed",   query: "Show my disbursed leads this month",        emoji: "✅" },
    { id: "this-month",  label: "This Month",  query: "How many leads did I add this month?",      emoji: "📅" },
    { id: "new-leads",   label: "New Today",   query: "Show my new leads added today",             emoji: "⚡" },
  ],
  manager: [
    { id: "top-bankers",   label: "Top Bankers",  query: "Which banker generated the highest disbursement this month?",  emoji: "🏆" },
    { id: "top-partners",  label: "Top Partners", query: "Top channel partners by leads this month",                     emoji: "🤝" },
    { id: "conversions",   label: "Conversions",  query: "What is the lead conversion rate this month?",                 emoji: "🔄" },
    { id: "pipeline",      label: "Pipeline",     query: "How many leads are in disbursal pending status?",              emoji: "📊" },
    { id: "this-month",    label: "This Month",   query: "Total disbursement amount this month",                         emoji: "💰" },
  ],
  bh: [
    { id: "all-disbursed", label: "Disbursements", query: "Total disbursement amount this month across all leads",       emoji: "💰" },
    { id: "top-bankers",   label: "Top Bankers",   query: "Top 5 bankers by disbursement this month",                    emoji: "🏆" },
    { id: "top-partners",  label: "Top Partners",  query: "Top 5 channel partners by leads this quarter",                emoji: "🤝" },
    { id: "trends",        label: "Trends",        query: "Show month-over-month disbursement trend for the last 6 months", emoji: "📈" },
    { id: "conversion",    label: "Conversion",    query: "What is the overall lead conversion rate this quarter?",      emoji: "🎯" },
  ],
};

const SUGGESTED_GROUPS: Record<RoleKey, SuggestedGroup[]> = {
  field: [
    {
      category: "My Leads",
      questions: [
        "Show my active leads",
        "Show my pending follow-up leads",
        "How many new leads do I have this week?",
        "Show my leads in Mumbai",
      ],
    },
    {
      category: "Disbursements",
      questions: [
        "Show my disbursed leads this month",
        "What is my total disbursed amount this month?",
      ],
    },
    {
      category: "Performance",
      questions: [
        "How many leads did I add this month?",
        "Show my sanctioned leads this week",
      ],
    },
  ],
  manager: [
    {
      category: "Rankings",
      questions: [
        "Which banker generated the highest disbursement this month?",
        "Top 5 channel partners by leads this month",
        "Which city has the most disbursed leads this quarter?",
      ],
    },
    {
      category: "Performance",
      questions: [
        "What is the lead conversion rate this month?",
        "How many leads are in disbursal pending status?",
        "Show leads sanctioned this week",
      ],
    },
    {
      category: "Disbursements",
      questions: [
        "Total disbursement amount this month",
        "Which banker disbursed the most in June?",
      ],
    },
  ],
  bh: [
    {
      category: "Disbursements",
      questions: [
        "Total disbursement amount this month",
        "Top 5 bankers by disbursement this month",
        "Show month-over-month disbursement for last 6 months",
      ],
    },
    {
      category: "Rankings",
      questions: [
        "Top 5 channel partners by leads this quarter",
        "Which banker has the highest conversion rate?",
        "Which city has the highest disbursement this month?",
      ],
    },
    {
      category: "Pipeline",
      questions: [
        "What is the overall lead conversion rate this quarter?",
        "How many leads are in each status this month?",
      ],
    },
  ],
};

// Partial stems for fast autocomplete as user types
const AUTOCOMPLETE_STEMS = [
  "Show my active leads",
  "Show my pending follow-up leads",
  "How many leads",
  "Top 5 bankers",
  "Which banker",
  "Total disbursement",
  "Conversion rate",
  "Show disbursed leads",
  "Sabse zyada disbursement",
  "Mera pending leads",
  "Is mahine kitne leads",
];

export function getQuickActions(primaryRole: string): QuickAction[] {
  return QUICK_ACTIONS[getRoleKey(primaryRole)];
}

export function getSuggestedGroups(primaryRole: string): SuggestedGroup[] {
  return SUGGESTED_GROUPS[getRoleKey(primaryRole)];
}

export function getAutocompleteSuggestions(
  input: string,
  primaryRole: string,
): string[] {
  if (input.length < 2) return [];
  const lower = input.toLowerCase();
  const allQuestions = getSuggestedGroups(primaryRole).flatMap((g) => g.questions);
  const allActions = getQuickActions(primaryRole).map((a) => a.query);
  const stems = AUTOCOMPLETE_STEMS.filter((s) =>
    s.toLowerCase().startsWith(lower),
  );
  const matches = [...allQuestions, ...allActions].filter(
    (q) =>
      q.toLowerCase().startsWith(lower) ||
      q.toLowerCase().includes(lower),
  );
  return [...new Set([...stems, ...matches])].slice(0, 4);
}
